// eslint-disable-next-line import/no-cycle
import { FormattedMessage, useIntl } from 'react-intl';
import { useContext, useState, ReactElement, useEffect } from 'react';
import { format, add } from 'date-fns';
import { useMedia } from 'react-use';
import AppContext from '../../../utils/createContext';
import {
  StyledMapNavigation,
  StyledIntroText,
  StyledSubmitButton,
  StyledAdditionalInformation,
  StyledCheckbox,
  StyledFlightSummary,
  StyledFlightTypeIndicator,
  StyledFlightLocation,
  StyledFlightType,
  StyledScrollWrapper,
} from './MapNavigation.styles';
import { Heading } from '../../atoms/Heading/Heading';
// eslint-disable-next-line import/no-cycle
import { LocationSelector } from '../../molecules/LocationSelector/LocationSelector';
import { Icon } from '../../atoms/Icon/Icon';
import { parseHtml } from '../../../utils/sanitiseHtml';
import { MediaQuery } from '../../../styles/mediaQuery';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __swaStationMessageAllowedTags: Array<string>;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __swaStationMessageAllowedAttributes: Array<string>;
  }
}

export function MapNavigation(): ReactElement {
  const {
    originAirport,
    setOriginAirport,
    destinationAirport,
    setDestinationAirport,
    flightType,
    nonstopChecked,
    setNonstopChecked,
  } = useContext(AppContext);
  const intl = useIntl();
  const [originSelectorOpen, setOriginSelectorOpen] = useState(false);
  const [destinationSelectorOpen, setDestinationSelectorOpen] = useState(false);

  useEffect(() => {
    if (!originAirport.iata_code) {
      setNonstopChecked(false);
    }
  });

  const nonstopChangeHandler = () => {
    setNonstopChecked(!nonstopChecked);
  };

  const connectingFlightChosen = !!(
    originAirport.iata_code &&
    destinationAirport.iata_code &&
    flightType !== 'nonstop'
  );

  const departureDate = format(add(Date.now(), { days: 60 }).setDate(1), 'yyyy-MM-dd');
  const returnDate = format(add(Date.now(), { days: 63 }).setDate(1), 'yyyy-MM-dd');

  const departureDateMobile = format(add(Date.now(), { days: 60 }).setDate(1), 'MM/dd/yyyy');
  const returnDateMobile = format(add(Date.now(), { days: 63 }).setDate(1), 'MM/dd/yyyy');
  const isMobile = useMedia(MediaQuery.XSmall);

  const submitUrl = () => {
    const currentUrl = window.location.href;
    const isMweb = new RegExp('channel=mweb', 'g');
    const isIos = new RegExp('channel=ios', 'g');
    const isAndroid = new RegExp('channel=android', 'g');
    const isIpad = new RegExp('channel=ipad', 'g');
    let result = '';

    if (!originAirport.airport_name || !destinationAirport.airport_name) {
      result = '/route-map/#map-section';
    } else if (isMweb.test(currentUrl) || isMobile) {
      result = `/air/low-fare-calendar/select-dates.html?adultPassengersCount=1&passengerType=ADULT&promoCode=&returnAirportCode=&currencyCode=USD&originationAirportCode=${originAirport.iata_code}&destinationAirportCode=${destinationAirport.iata_code}&departureDate=${departureDate}&returnDate=${returnDate}&tripType=roundtrip&int=rtmaplfc&dummyvar1=${departureDateMobile}&dummyVar2=${returnDateMobile}&useLowFareCalendar=true&lapInfantPassengersCount=0&clk=ROUTE_MAP_FIND_FLIGHTS_${originAirport.iata_code}_${destinationAirport.iata_code}`;
    } else if (isIos.test(currentUrl) || isAndroid.test(currentUrl) || isIpad.test(currentUrl)) {
      result = `app:airbooking?fromCity=${originAirport.iata_code}&toCity=${destinationAirport.iata_code}&tripType=RT&INT=rtmaplfc&departDate=${departureDate}&returnDate=${returnDate}`;
    } else {
      result = `/air/low-fare-calendar/select-dates.html?adultPassengersCount=1&lapInfantPassengersCount=0&passengerType=ADULT&promoCode=&returnAirportCode=&currencyCode=USD&originationAirportCode=${originAirport.iata_code}&destinationAirportCode=${destinationAirport.iata_code}&departureDate=${departureDate}&returnDate=${returnDate}&tripType=roundtrip&int=rtmaplfc&clk=ROUTE_MAP_FIND_FLIGHTS_${originAirport.iata_code}_${destinationAirport.iata_code}`;
    }
    return result;
  };

  return (
    <StyledMapNavigation>
      <StyledScrollWrapper $openSelector={originSelectorOpen || destinationSelectorOpen}>
        <div>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <Heading id="navigation-heading">
            <FormattedMessage
              id="navigation-heading"
              description="heading"
              defaultMessage="Southwest Airlines Route Map"
            />
          </Heading>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <StyledIntroText id="navigation-intro-text">
            <FormattedMessage
              id="navigation-intro-text"
              description="intro text"
              defaultMessage="Where do you wanna go? Use our interactive map to find your next flight."
            />
          </StyledIntroText>
          <LocationSelector
            isOpen={originSelectorOpen}
            setIsOpen={setOriginSelectorOpen}
            label={intl.formatMessage({
              id: 'navigation-depart-label',
              description: 'Depart label',
              defaultMessage: 'Depart',
            })}
            placeholder={intl.formatMessage({
              id: 'navigation-depart-placeholder',
              description: 'Depart placeholder',
              defaultMessage: 'From',
            })}
            overlayLabel={intl.formatMessage({
              id: 'navigation-depart-overlay-label',
              description: 'Depart overlay label',
              defaultMessage: 'Departure Locations',
            })}
            isDeparture
            location={originAirport}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            setLocation={setOriginAirport}
          />
          <LocationSelector
            isOpen={destinationSelectorOpen}
            setIsOpen={setDestinationSelectorOpen}
            label={intl.formatMessage({
              id: 'navigation-arrive-label',
              description: 'Arrive label',
              defaultMessage: 'Arrive',
            })}
            placeholder={intl.formatMessage({
              id: 'navigation-arrive-placeholder',
              description: 'Arrive placeholder',
              defaultMessage: 'To',
            })}
            overlayLabel={intl.formatMessage({
              id: 'navigation-arrive-overlay-label',
              description: 'Arrive overlay label',
              defaultMessage: 'Arrival Locations',
            })}
            location={destinationAirport}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            setLocation={setDestinationAirport}
          />
          <StyledCheckbox
            label={intl.formatMessage({
              id: 'navigation-checkbox-nonstop',
              defaultMessage: 'Nonstop only ',
              description: 'Nonstop label',
            })}
            name="nonstop"
            onChange={nonstopChangeHandler}
            checked={nonstopChecked}
            disabled={!originAirport.iata_code || connectingFlightChosen}
          />
          {originAirport.airport_name && destinationAirport.airport_name && (
            <StyledFlightSummary id="navigation-flight-summary">
              <StyledFlightTypeIndicator
                id="navigation-flight-summary-illustration"
                $flightType={flightType}
              >
                <Icon.Airplane />
              </StyledFlightTypeIndicator>
              <div>
                <StyledFlightLocation id="navigation-flight-depart">
                  {originAirport.airport_name}
                  <span>{originAirport.booking_widget_display_name}</span>
                </StyledFlightLocation>
                <StyledFlightType id="navigation-flight-type">
                  {flightType === 'connected' ? (
                    <FormattedMessage
                      id="navigation-flight-type-connecting"
                      description="flight type information connecting"
                      defaultMessage="Connecting flight"
                    />
                  ) : (
                    <FormattedMessage
                      id="navigation-flight-type-nonstop"
                      description="flight type information nonstop"
                      defaultMessage="Nonstop flight"
                    />
                  )}
                </StyledFlightType>
                <StyledFlightLocation id="navigation-flight-arrive">
                  {destinationAirport.airport_name}
                  <span>{destinationAirport.booking_widget_display_name}</span>
                </StyledFlightLocation>
              </div>
            </StyledFlightSummary>
          )}
        </div>
        <StyledAdditionalInformation>
          {originAirport.station_message && (
            <div dangerouslySetInnerHTML={parseHtml(originAirport.station_message)} />
          )}
          {destinationAirport.station_message && (
            <>
              <br />
              <div dangerouslySetInnerHTML={parseHtml(destinationAirport.station_message)} />
            </>
          )}
        </StyledAdditionalInformation>

        <StyledSubmitButton
          aria-label="Find flights"
          id="find-flights-button"
          disabled={!originAirport.airport_name || !destinationAirport.airport_name}
          tabIndex={
            !originAirport.airport_name || !destinationAirport.airport_name ? -1 : undefined
          }
          href={submitUrl()}
          enforceLink
        >
          <FormattedMessage
            id="navigation-button-submit"
            description="submit button"
            defaultMessage="Find flights"
          />
        </StyledSubmitButton>
      </StyledScrollWrapper>
    </StyledMapNavigation>
  );
}
